// EL MOTOR DELS BUCLES CAUSALS.
//
// Viu fora de React i sense cap import, a posta: així es pot fer córrer sol i comprovar que fa el
// que ha de fer. Un motor de simulació que només es pot mirar per la pantalla no es pot provar, i
// el que hi havia abans en va ser l'exemple: prometia tres coses i no en feia cap.
//
// ── QUÈ HI HAVIA I QUÈ FALLAVA ──────────────────────────────────────────────────────────────
//
// L'anterior calculava una cosa —no era façana— però tenia tres problemes de fons:
//
//   1. **El retard no s'usava.** `rel.delay` només servia per dibuixar una marca al lligam. I el
//      retard és justament el que fa que un bucle equilibrador OSCIL·LI en comptes de convergir
//      suaument: és la lliçó central de la disciplina, i el motor la ignorava.
//   2. **El pas de temps era la freqüència del monitor.** El canvi s'aplicava sencer a cada
//      fotograma, sense dividir per cap `dt`. En una pantalla de 120 Hz el model corria el doble
//      de ràpid que en una de 60 — inacceptable per a una aula.
//   3. **El control de velocitat no tocava el càlcul**, només el rellotge de la pantalla.
//
// I dos més de disseny: els valors es retallaven a [10, 90] amb una atracció cap a 50, cosa que
// aplanava precisament les dues conductes que un diagrama causal ha d'ensenyar —el bucle que es
// dispara i el que busca el seu nivell—.
//
// ── QUÈ FA ARA ──────────────────────────────────────────────────────────────────────────────
//
// Per a cada variable v, amb x entre 0 i 100:
//
//     dx[v]/dτ = GUANY · Σ  força(s→v) · signe(s→v) · ( x[s](τ − retard) / 100 ) · sat
//                       s→v
//
//     sat = (1 − x[v]/100)   si el lligam és positiu     ← queda espai per pujar
//           (x[v]/100)       si és negatiu               ← queda quantitat per baixar
//
// **No hi ha cap terme més, i això és una decisió.** El motor vell n'hi afegia un que estirava
// TOTHOM cap a 50, un número que no vol dir res, i era el que aplanava totes les conductes. Es va
// provar de substituir-lo per una atracció cap al valor inicial de cada variable, i tampoc: amb
// això un bucle reforçador ja no es pot disparar mai, perquè l'atracció el frena abans d'arribar
// a dalt.
//
// Ara el model **fa el que diu el dibuix i res més**. Una variable sense lligams d'entrada no es
// mou —la seva derivada és zero—, o sigui que les variables exògenes es mantenen soles sense cap
// mecanisme especial. I si un bucle equilibrador et col·lapsa a zero, no és cap error del motor:
// és que al dibuix hi falta el que empenyia en sentit contrari.
//
// El terme de saturació és el que ja hi havia i és el tros ben pensat: dona la forma del
// creixement logístic. Un bucle reforçador creix a poc a poc, s'accelera i satura a 100 en
// comptes de disparar-se a l'infinit; un d'equilibrador estira cap al seu nivell.
//
// **El retard és pur**: el que arriba de la font és el valor que tenia fa `retard` unitats de
// temps, no el d'ara. Per això cal guardar la història. Un retard dins d'un bucle equilibrador
// dona oscil·lació i sobrepassament, que és el que ha de passar.
//
// La integració és Euler explícit amb pas fix. Amb aquestes magnituds i aquest pas és estable de
// sobres; si algun dia el guany puja molt, caldria Runge-Kutta o un pas més petit.

// Pas d'integració, en unitats de temps del model. Fix i independent del monitor.
export const DT = 0.05;

// Quantes unitats de temps del model passen per segon real quan la velocitat és 1.
export const UNITATS_PER_SEGON = 1;

// Guany global. No surt de cap teoria: és la constant que fa que una estructura corrent es vegi
// moure en uns deu segons a velocitat 1. Canviar-la accelera o alenteix TOT per igual, sense
// canviar quina forma tenen les corbes.
export const GUANY = 12;


// Sostre de passes per fotograma. Si la pestanya ha estat amagada una estona, `requestAnimationFrame`
// torna amb molt de temps acumulat; sense sostre, el navegador es quedaria clavat intentant
// posar-se al dia.
const MAX_PASSES = 40;

const FORCES = { weak: 0.5, medium: 1, moderate: 1, strong: 1.5 };

const aco = (x) => Math.max(0, Math.min(100, x));

// L'estat és tot el que cal per continuar: els valors d'ara, el temps del model, i la història
// de cada variable perquè els retards puguin mirar enrere.
export function estatInicial(variables) {
  const valors = {};
  const historia = {};
  for (const v of variables) {
    const x = aco(Number(v.properties?.initialValue ?? 50));
    valors[v.id] = x;
    historia[v.id] = [x];
  }
  // `resta` és el temps de model demanat que encara no arriba a una passa sencera. Sense
  // guardar-lo, a 60 Hz cada fotograma en demana un terç, s'arrodoneix a zero i **el model no
  // avança mai**. Va passar, i la prova del motor és qui ho va trobar.
  return { valors, historia, temps: 0, resta: 0 };
}

// Quant enrere pot mirar la història. El retard més llarg del dibuix, i prou: guardar-ho tot
// seria fer créixer la memòria sense fi mentre la simulació corre.
function passesDeMemoria(relationships) {
  const maxRetard = relationships.reduce((m, r) => Math.max(m, Number(r.delay) || 0), 0);
  return Math.ceil(maxRetard / DT) + 2;
}

// El valor que la font «entrega» ara: el d'ara si no hi ha retard, o el que tenia fa `retard`
// unitats de temps si n'hi ha.
function valorEntregat(estat, id, retard) {
  const h = estat.historia[id];
  if (!h || !h.length) return 0;
  const passes = Math.round((Number(retard) || 0) / DT);
  if (passes <= 0) return h[h.length - 1];
  const i = h.length - 1 - passes;
  // Encara no fa prou estona que corre: s'entrega el valor més antic que es coneix, que és el
  // que la variable tenia al principi. És el que passaria de debò.
  return i >= 0 ? h[i] : h[0];
}

// Diu si `r` (negativa) té una relació de tornada entre el mateix parell, també negativa:
// `A→B(-)` i `B→A(-)` alhora. És el patró que fa que la fórmula de sempre doni el mateix
// número als dos costats; qualsevol altra combinació (una sola relació, o dues de signes
// diferents) no hi topa.
function teMutua(relationships, r) {
  return relationships.some((x) => x !== r && x.polarity === '-' && x.source === r.target && x.target === r.source);
}

// Una passa de DT.
export function passa(estat, variables, relationships) {
  const canvis = {};
  for (const v of variables) canvis[v.id] = 0;

  for (const r of relationships) {
    if (canvis[r.target] === undefined || estat.valors[r.source] === undefined) continue;
    const font = valorEntregat(estat, r.source, r.delay) / 100;
    const signe = r.polarity === '-' ? -1 : 1;
    const forca = FORCES[r.strength] ?? 1;
    const desti = estat.valors[r.target];
    let sat;
    if (signe > 0) {
      sat = 1 - desti / 100;
    } else if (teMutua(relationships, r)) {
      // COMPETICIÓ MÚTUA: dues variables que es claven una negativa l'una a l'altra.
      // `desti/100` —el de sempre— dona el MATEIX número als dos costats (és el mateix
      // producte, `font·desti`, escrit a l'inrevés), i amb el mateix número mai no pot
      // sortir-ne un guanyador: qualsevol diferència s'esborra sola, comprovat fent córrer
      // «Èxit per als qui ja triomfen» amb gaps de fins a 20 punts i sempre acabava en
      // empat. Aquí, en comptes del valor absolut del destí, es fa servir la QUOTA de la
      // font sobre el conjunt dels dos: qui ja en té més, en pren més de l'altre. A la
      // simetria exacta (font=desti) dona el mateix número que abans —un empat exacte es
      // queda empatat, que és correcte: un sistema perfectament simètric no té cap raó per
      // decantar-se—, però qualsevol diferència, per petita que sigui, ara s'amplifica en
      // comptes d'esborrar-se. **Només quan hi ha una relació negativa de tornada entre el
      // mateix parell**: un bucle equilibrador normal (una sola relació negativa, sense
      // tornada) fa servir exactament la fórmula de sempre i no canvia (31/10/2026).
      const fontRaw = estat.valors[r.source];
      const total = fontRaw + desti;
      sat = total > 0 ? fontRaw / total : 0.5;
    } else {
      sat = desti / 100;
    }
    canvis[r.target] += GUANY * forca * signe * font * sat;
  }

  const valors = {};
  const historia = {};
  const memoria = passesDeMemoria(relationships);
  for (const v of variables) {
    valors[v.id] = aco(estat.valors[v.id] + canvis[v.id] * DT);
    const h = estat.historia[v.id] ? estat.historia[v.id].concat(valors[v.id]) : [valors[v.id]];
    historia[v.id] = h.length > memoria ? h.slice(h.length - memoria) : h;
  }
  return { valors, historia, temps: estat.temps + DT, resta: estat.resta || 0 };
}

// Avança el temps de model demanat, en passes de DT.
//
// Aquí és on la velocitat entra al CÀLCUL i no només al rellotge, i on el resultat deixa de
// dependre de la freqüència del monitor: el que mana és quant temps real ha passat.
export function avanca(estat, variables, relationships, segonsReals, velocitat = 1) {
  let acumulat = (estat.resta || 0) + segonsReals * UNITATS_PER_SEGON * velocitat;
  const passes = Math.min(MAX_PASSES, Math.floor(acumulat / DT));
  let e = estat;
  for (let i = 0; i < passes; i++) e = passa(e, variables, relationships);
  acumulat -= passes * DT;
  // Si s'ha topat amb el sostre —la pestanya ha estat amagada una estona— la resta es llença.
  // Val més perdre temps de model que quedar-se clavat posant-se al dia.
  return { ...e, resta: passes === MAX_PASSES ? 0 : acumulat };
}
